import type { CSSProperties, MouseEvent } from "react";
import type { User } from "../../types/Types";
import type { ConnectionTarget } from "../../utils/connectionUserUtils";

export type ConnectionState = "connected" | "sent" | "received" | "none";

export interface AttendeeWithState {
  user: User;
  actionUser: User;
  connectionState: ConnectionState;
}

export interface EventAttendeeCardProps {
  attendee: AttendeeWithState;
  sequentialNumber: number;
  isCurrentUser: boolean;
  currentUserMembership: ConnectionTarget | string | undefined;
  explorerLimitReached?: boolean;
  isExplorerUser?: boolean;
  onBlockedConnectionAction?: () => void;
  onViewProfile: (user: User) => void;
  onSaveNote?: (user: User) => void;
  onMessage: (id: number) => void;
  onConnect: (user: User) => void;
  onCancelRequest: (id: number) => void;
  onAccept: (id: number, user: User) => void;
  onIgnore: (id: number) => void;
  onRemoveConnection: (user: User) => void;
  sendConnectionId: number | null;
  cancellingId: number | null;
  hasNote?: boolean;
  /** Clicked / highlighted row */
  isSelected?: boolean;
  onSelect?: () => void;
}

/** Desktop column sizing — flexible so action buttons never clip. */
export const COL = {
  index: 28,
  avatar: 48,
  name: 150,
  city: 130,
  objective: 160,
  membership: 140,
  btn: 136,
} as const;

export const GAP = 10;
export const BTN_HEIGHT = 38;
export const BTN_GAP = 8;

/** Shared geometry for every action button in the pair. */
export const ACTION_BTN_BASE: CSSProperties = {
  height: BTN_HEIGHT,
  minHeight: BTN_HEIGHT,
  maxHeight: BTN_HEIGHT,
  padding: "0 12px",
  fontSize: 12.5,
  lineHeight: 1.2,
  fontWeight: 500,
  boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
};

export const PROFILE_BTN_STYLE: CSSProperties = {
  ...ACTION_BTN_BASE,
  background: "#f8f9fa",
  border: "none",
  color: "#212529",
};

export const CONNECT_BTN_STYLE: CSSProperties = {
  ...ACTION_BTN_BASE,
  background: "linear-gradient(90deg, #2366CD 0%, #3A97A4 100%)",
  border: "none",
  color: "#fff",
};

export const UPGRADE_BTN_STYLE: CSSProperties = {
  ...ACTION_BTN_BASE,
  border: "none",
  borderLeft: "4px solid #0d6efd",
  background: "#fff",
  color: "#6c757d",
};

export const CANCEL_BTN_STYLE: CSSProperties = {
  height: BTN_HEIGHT,
  minHeight: BTN_HEIGHT,
  maxHeight: BTN_HEIGHT,
  padding: "0 12px",
  fontSize: 12.5,
  lineHeight: 1.2,
  fontWeight: 500,
  boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
};

export const BTN_SLOT: CSSProperties = {
  width: COL.btn,
  minWidth: COL.btn,
  maxWidth: COL.btn,
};

export const colStyle = (basis: number, grow = 1): CSSProperties => ({
  flex: `${grow} 1 ${basis}px`,
  minWidth: 0,
});

export const AVATAR_STYLE: CSSProperties = {
  width: COL.avatar,
  height: COL.avatar,
  objectFit: "cover",
};

export const AVATAR_MOBILE_STYLE: CSSProperties = {
  width: 56,
  height: 56,
  objectFit: "cover",
};

export const ACTION_BTN_CLASS =
  "w-100 d-flex align-items-center justify-content-center rounded-2 text-nowrap border-0 shadow-sm";

export const stopCardSelect = (e: MouseEvent) => e.stopPropagation();
